import type { AxiosRequestConfig } from 'axios';
import PythonAIAPI from './PythonAIApiBase';
import API from './ApiBase';
import { CreateEUDeclarationResponse, UpdateEUDeclarationPayload } from '../types/EUDeclaration.types';
import { AIGenerateJobResponse } from '../types/AIJob.types';

type ApiRequestConfig = AxiosRequestConfig & { skipErrorToast?: boolean };

export interface CompleteEUDeclarationResponse {
  success: boolean;
  missingFields?: string[];
  requiresSignature?: boolean;
}

export interface EUDeclarationFillResult {
  declarationId: string;
  updatedFields: string[];
  contentJson?: Record<string, any>;
}

export interface EUDeclarationFillResponse {
  success: boolean;
  data: EUDeclarationFillResult;
}

export const createEUDeclarationDraft = async (aiSystemId: string, annexIVId?: string) => {
  const payload = {
    aiSystemId,
    annexIVId: annexIVId || undefined,
  };

  const response = await API.post<CreateEUDeclarationResponse>(
    `/eu-declaration-of-conformity`,
    payload
  );
  return response.data;
};

export const getEUDeclarationByAiSystem = async (
  aiSystemId: string,
  options?: { suppressErrorToast?: boolean }
) => {
  const config: ApiRequestConfig = {};

  if (options?.suppressErrorToast) {
    config.skipErrorToast = true;
  }

  const response = await API.get<CreateEUDeclarationResponse>(
    `/eu-declaration-of-conformity/ai-system/${aiSystemId}`,
    config
  );
  return response.data;
};

export const suggestEUDeclarationContent = async (declarationId: string): Promise<EUDeclarationFillResponse> => {
  const response = await PythonAIAPI.patch<EUDeclarationFillResponse>(
    `/euDeclaration/${declarationId}`
  );
  return response.data;
};

export const generateEUDeclarationContent = async (
  declarationId: string,
  forceRefresh: boolean = false,
  options?: { suppressErrorToast?: boolean }
): Promise<AIGenerateJobResponse> => {
  const config: ApiRequestConfig = {};

  if (options?.suppressErrorToast) {
    config.skipErrorToast = true;
  }

  const response = await API.post<AIGenerateJobResponse>(
    `/eu-declaration-of-conformity/${declarationId}/generate`,
    { forceRefresh },
    config
  );
  return response.data;
};

export const completeEUDeclaration = async (
  declarationId: string,
  payload: UpdateEUDeclarationPayload
): Promise<CompleteEUDeclarationResponse> => {
  const response = await API.put<CompleteEUDeclarationResponse>(
    `/eu-declaration-of-conformity/${declarationId}/finish`,
    payload
  );
  return response.data;
};

export const updateEUDeclaration = async (
  declarationId: string,
  payload: UpdateEUDeclarationPayload
) => {
  const response = await API.put<CreateEUDeclarationResponse>(
    `/eu-declaration-of-conformity/${declarationId}`,
    payload
  );
  return response.data;
};
